import React from 'react';

import {NoPreviewPlaceholder} from 'analysis-tool/ui/view-templates/StepConfigurationViewTemplate';
import {DataFrame} from 'data-frames/DataFrameTypes';
import DataFrameWarningBanner from 'data-frames/graphql/DataFrameWarningBanner';
import DataFrameTableWrapper from 'data-frames/wrapper/DataFrameTableWrapper';
import {ActionOrigin, PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {PreviewOutput, PreviewStepParams} from 'pipelines/network/PipelineDAO';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {AsyncState, AsyncStatus} from 'ui-library/hooks/useAsyncState';
import LoadingOverlay from 'ui-library/LoadingOverlay';

import {DATA_FRAME_CREATION_NO_PREVIEW_PLACEHOLDER_TEXT} from './DataFrameCreationConstants';

export interface DataFrameTablePreviewProps<StepType extends PipelineStepType> {
  clientStateId: string;
  errorMessage?: string;
  getDataFrameFromOutput: (output: PreviewOutput<StepType>) => DataFrame;
  noPreviewSubtitle?: string;
  noPreviewTitle?: string;
  previewState: AsyncState<
    PreviewOutput<StepType>,
    [PreviewStepParams<PipelineStepType.JOIN> | PreviewStepParams<PipelineStepType.UNION_DATASETS>]
  >;
}

export default function DataFrameTablePreview<StepType extends PipelineStepType>(
  props: DataFrameTablePreviewProps<StepType>
): JSX.Element {
  if (props.previewState.status === AsyncStatus.LOADING) {
    return (
      <LoadingOverlay isLoading={true} isFaded={true}>
        {props.previewState.previousData == null ? (
          <DataFrameTablePreview {...props} previewState={{status: AsyncStatus.NOT_FETCHED}} />
        ) : (
          <DataFrameTablePreview
            {...props}
            previewState={{status: AsyncStatus.SUCCESS, data: props.previewState.previousData}}
          />
        )}
      </LoadingOverlay>
    );
  }

  if (props.previewState.status === AsyncStatus.NOT_FETCHED) {
    return (
      <NoPreviewPlaceholder
        title={props.noPreviewTitle ?? DATA_FRAME_CREATION_NO_PREVIEW_PLACEHOLDER_TEXT}
        subtitle={props.noPreviewSubtitle}
      />
    );
  }

  if (props.previewState.status === AsyncStatus.ERROR) {
    return (
      <InlineBanner
        bannerType={InlineBannerType.ERROR}
        text={props.errorMessage ?? 'Failed to load preview'}
        supportingInfo={props.previewState.responseJSON.userMessage ?? 'Please check your configuration.'}
      />
    );
  }

  const dataFrame = props.getDataFrameFromOutput(props.previewState.data);
  const banner = (
    <DataFrameWarningBanner
      canDownloadCompleteData={false}
      isRandomSubset={false}
      name={dataFrame.name}
      renderedColumns={dataFrame.columns.length}
      renderedRows={dataFrame.numRows ?? dataFrame.rows.length}
      totalColumns={dataFrame.columns.length}
      totalRows={dataFrame.numRows ?? dataFrame.rows.length}
    />
  );

  return (
    <DataFrameTableWrapper
      actionOrigin={ActionOrigin.ANALYSIS_TOOL}
      banner={banner}
      clientStateId={`${props.clientStateId}-combine-tables-output-preview`}
      dataFrame={dataFrame}
      renderIsExcludedColumn={false}
    />
  );
}
